import {useCallback, useEffect, useRef, useState} from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  Skeleton,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useBreakpointValue,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  TablerSend,
  TablerCircleCheck,
  TablerLock,
  TablerUsers,
  TablerPhone,
  TablerWand,
} from '../../icons';
import usePlannerStyles from '../planner/usePlannerStyles';
import {useUser} from '../../contexts/UserContext';
import useCreateSessionPatients from '../planner/useCreateSessionPatients';
import {PatientSection} from '../planner/CreateSessionFormSections';
import {patientDisplayName} from '../planner/createSessionFormUtils';
import {isNonEnglish, languageFlag} from '../../constants/designTokens';
import {getEhrBrandLabel} from '../../constants/ehrBrand';
import {
  RECAP_PRIMARY_BUTTON_PROPS,
  RECAP_GRAY_SECONDARY_BUTTON_PROPS,
} from '../common/recapButtonStyles';
import {RecapSectionHeading} from '../common/SessionDetailView';
import DetailBackButton from '../common/DetailBackButton';
import MetaPill from '../common/MetaPill';
import {ehrService} from '../../services/ehrService';
import {patientService} from '../../services/patientService';
import {notifyPhoneCallPatientLink} from '../planner/usePlannerPhoneCalls';
import {
  getReasonPresets,
  getFacilities,
  getProviders,
  getCareTeam,
  getAssignableStaff,
  saveTelephoneEncounter,
  addAction,
  assignTo,
  setFacility,
  pushToEhr,
  closeTelephoneEncounter,
  generateTelephoneEncounterText,
  editTelephoneEncounterText,
  simplifyTelephoneEncounterMessage,
  findOpenTelephoneEncounters,
} from '../../services/telephoneEncounterService';
import type {
  CareTeamMember,
  FacilityOption,
  ReasonPreset,
  TelephoneEncounter,
  TranslatorInfo,
  OpenTelephoneEncounterSummary,
} from '../../types/telephoneEncounter';
import {
  TeSectionCard,
  DetailFormRow,
  TE_GENERATED_REVEAL_SX,
} from './telephoneEncounterUi';
import OpenTelephoneEncounterPicker from './OpenTelephoneEncounterPicker';
import ReasonField from './ReasonField';
import CareTeamSelect from './CareTeamSelect';
import ActionTakenThread from './ActionTakenThread';
import TeDictationButton from './TeDictationButton';
import TelephoneEncounterPushSuccessModal from './TelephoneEncounterPushSuccessModal';
import PhoneCallTranscript from '../session/PhoneCallTranscript';
import DictationEditFlash from '../common/DictationEditFlash';

// Voice-edit result flash matches the note-preview lifetime: the shine sweep
// self-fades ~1.7s in, and we unmount shortly after so a re-edit replays it.
const VOICE_EDIT_FLASH_MS = 3500;

// Dictating a full Message (lab results read line by line) legitimately runs
// past the hook's default 2-minute voice-edit cap.
const MESSAGE_DICTATION_MAX_MS = 5 * 60 * 1000;

function formatTimestamp(value: any) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date} · ${time}`;
}

const STATUS_LABELS: Record<TelephoneEncounter['status'], string> = {
  draft: 'Draft',
  open: 'Open',
  closed: 'Closed',
};

interface CurrentUser {
  id: string | null;
  name: string | null;
  role: string | null;
}

// The unlinked-create patient typeahead. Reuses the New Encounter form's
// PatientSection + patient-search hook so picking a patient feels identical to
// starting an encounter. Only rendered when a brand-new TE has no patient yet.
const TelephoneEncounterPatientPicker = ({
  onSelect,
}: {
  onSelect: (item: any) => void;
}) => {
  const {userData} = useUser();
  const [patientSearch, setPatientSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPatientItem, setSelectedPatientItem] = useState<any>(null);
  const patientTypeaheadRef = useRef<any>(null);

  const {filteredPatients, isLoadingPatients, isSearchingEhrPatients} =
    useCreateSessionPatients({userData, patientSearch});

  const handleSelectPatient = (item: any) => {
    setSelectedPatientItem(item);
    setIsOpen(false);
    setPatientSearch('');
    onSelect(item);
  };

  const handleClearPatient = () => {
    setSelectedPatientItem(null);
    setPatientSearch('');
    onSelect(null);
  };

  return (
    <Box
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setIsOpen(false);
        }
      }}
    >
      <PatientSection
        showHeading={false}
        filteredPatients={filteredPatients}
        isLoadingPatients={isLoadingPatients}
        isSearchingEhrPatients={isSearchingEhrPatients}
        handlePatientSearchChange={(event: any) =>
          setPatientSearch(event.target.value)
        }
        handleSelectPatient={handleSelectPatient}
        handleClearPatient={handleClearPatient}
        handleEditPatient={() => {
          setSelectedPatientItem(null);
          setIsOpen(true);
        }}
        onPatientSearchFocus={() => setIsOpen(true)}
        patientSearch={patientSearch}
        patientTypeaheadRef={patientTypeaheadRef}
        selectedPatientItem={selectedPatientItem}
        selectedPatientName={
          selectedPatientItem ? patientDisplayName(selectedPatientItem) : ''
        }
        shouldShowResults={isOpen}
        portalResults
      />
    </Box>
  );
};

// Entrance for the detail body. Mirrors the encounter detail view's
// skeleton → content cross-fade (`session-skeleton-fade-in`): the body lifts a
// few pixels while fading in so the form reveals smoothly instead of popping in
// once the reference data + open-encounter check resolve.
const TE_DETAIL_FADE_IN_SX = {
  '@keyframes te-detail-fade-in': {
    from: {opacity: 0, transform: 'translateY(4px)'},
    to: {opacity: 1, transform: 'translateY(0)'},
  },
} as const;
const TE_DETAIL_FADE_IN_ANIMATION = 'te-detail-fade-in 320ms ease-out both';

// Body placeholder shown while the reference data (reasons / providers / care
// team) loads. Keeps the section headings and card geometry stable so the
// layout never shifts — the shimmer simply cross-fades into the real form,
// matching the encounter detail view's loading treatment.
const TelephoneEncounterDetailSkeleton = ({
  showInterpreter,
  showTranscript,
}: {
  showInterpreter: boolean;
  showTranscript: boolean;
}) => {
  const {borderColor} = usePlannerStyles();
  const rows = [
    {labelW: '4.5rem', controlH: '44px'},
    {labelW: '4rem', controlH: '44px'},
    {labelW: '5rem', controlH: '96px'},
    {labelW: '4.75rem', controlH: '44px'},
  ];
  return (
    <VStack
      spacing={4}
      align="stretch"
      sx={TE_DETAIL_FADE_IN_SX}
      animation={TE_DETAIL_FADE_IN_ANIMATION}
    >
      <Box as="section">
        <RecapSectionHeading label="Details" />
        <TeSectionCard>
          <VStack spacing={4} align="stretch">
            {rows.map((row, idx) => (
              <Box
                key={idx}
                borderTopWidth={idx === 0 ? 0 : '1px'}
                borderColor={borderColor}
                pt={idx === 0 ? 0 : 4}
              >
                <Flex
                  gap={{base: 2, md: 3}}
                  align={{base: 'stretch', md: 'center'}}
                  direction={{base: 'column', md: 'row'}}
                >
                  <Skeleton h="0.9rem" w={row.labelW} borderRadius="md" />
                  <Box flex="1" minW={0}>
                    <Skeleton h={row.controlH} w="100%" borderRadius="lg" />
                  </Box>
                </Flex>
              </Box>
            ))}
          </VStack>
        </TeSectionCard>
      </Box>

      {showInterpreter ? (
        <Box as="section">
          <RecapSectionHeading label="Interpreter" />
          <TeSectionCard>
            <VStack align="stretch" spacing={3}>
              {[0, 1, 2].map(i => (
                <Flex key={i} justify="space-between" gap={3}>
                  <Skeleton h="0.9rem" w="5rem" borderRadius="md" />
                  <Skeleton h="0.9rem" w="7rem" borderRadius="md" />
                </Flex>
              ))}
            </VStack>
          </TeSectionCard>
        </Box>
      ) : null}

      {showTranscript ? (
        <Box as="section">
          <RecapSectionHeading label="Transcript" />
          <TeSectionCard>
            <VStack align="stretch" spacing={3}>
              <Skeleton h="0.8rem" w="40%" borderRadius="md" />
              <Skeleton h="0.8rem" w="90%" borderRadius="md" />
              <Skeleton h="0.8rem" w="75%" borderRadius="md" />
            </VStack>
          </TeSectionCard>
        </Box>
      ) : null}
    </VStack>
  );
};

// Standalone, editable telephone-encounter view. Shares the regular encounter
// detail's header anatomy (date -> title -> meta -> status pill + primary
// action) and the New Encounter composer's labelled form rows, so it reads as
// the same product surface as the rest of the planner.
//
// The form is mode-aware, keyed off status:
//   - draft  => CREATE: Reason (preset select) + Message (generated). The
//               registrar composes and pushes it.
//   - open   => EDIT: no Reason/Message; the Action Taken thread takes over and
//               the encounter is worked + reassigned until it's closed.
//   - closed => locked.
const TelephoneEncounterDetailView = ({
  telephoneEncounter,
  companyId = null,
  currentUser,
  onBack,
  onUpdated,
  onEditExisting,
  onPushed,
}: {
  telephoneEncounter: TelephoneEncounter;
  companyId?: string | null;
  currentUser?: CurrentUser;
  onBack?: () => void;
  onUpdated?: (te: TelephoneEncounter) => void;
  // Called when the registrar chooses to edit an already-open telephone
  // encounter surfaced by the patient check (the parent adopts + opens it).
  // The originating call context (the call they just had) is forwarded so the
  // adopted encounter can generate its Action Taken note from that transcript.
  onEditExisting?: (
    summary: OpenTelephoneEncounterSummary,
    originatingCall?: {
      sourceCallId: string | null;
      translator: TranslatorInfo | null;
    },
  ) => void;
  // Called once a successful push is acknowledged, so the parent can land the
  // registrar on the pushed patient's history (mirrors the note-push return).
  onPushed?: (te: TelephoneEncounter) => void;
}) => {
  const isNarrow = useBreakpointValue({base: true, md: false});
  const toast = useToast();
  const styles = usePlannerStyles();
  const {pageBg, borderColor, textColor, subtleText} = styles;
  const bodyText = useColorModeValue('gray.900', 'whiteAlpha.900');

  const draftBadgeBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const draftBadgeColor = useColorModeValue('gray.700', 'gray.200');
  const closedBadgeColor = useColorModeValue('gray.600', 'gray.300');
  // The Phone chip reads as quiet metadata — same neutral grey the planner
  // search/list rows use — not a colored accent.
  const phonePillTone = styles.pillTones.neutral;

  const [te, setTe] = useState<TelephoneEncounter>(telephoneEncounter);
  // Drives the body skeleton → form cross-fade while the reference data loads.
  const [referenceLoading, setReferenceLoading] = useState(true);
  const [presets, setPresets] = useState<ReasonPreset[]>([]);
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [providers, setProviders] = useState<CareTeamMember[]>([]);
  const [careTeam, setCareTeam] = useState<CareTeamMember[]>([]);
  // True while a debounced server-side staff lookup (the assign-to picker's
  // typeahead) is in flight, so the dropdown can show a "Searching…" hint.
  const [staffSearching, setStaffSearching] = useState(false);
  // Tracks the latest staff search so a slow earlier response can't clobber a
  // newer one (out-of-order resolution).
  const staffSearchSeqRef = useRef(0);
  const [pushing, setPushing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [ehrBrandLabel, setEhrBrandLabel] = useState('the EHR');
  // Shows the "pushed to {EHR}" confirmation after a successful push, mirroring
  // the clinical-note push success surface.
  const [showPushSuccess, setShowPushSuccess] = useState(false);
  const [addingAction, setAddingAction] = useState(false);
  const [isGeneratingMessage, setIsGeneratingMessage] = useState(false);
  // Plays the one-shot reveal on the Message field the moment the generated
  // text lands, then clears itself on animation end so later edits don't replay.
  const [messageJustGenerated, setMessageJustGenerated] = useState(false);
  // Drives the note-preview-style "voice edit applied" shine flash over the
  // Message field. The epoch re-keys the overlay so each voice edit replays it.
  const [messageFlashEpoch, setMessageFlashEpoch] = useState(0);
  const messageFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(
    () => () => {
      if (messageFlashTimerRef.current)
        clearTimeout(messageFlashTimerRef.current);
    },
    [],
  );

  const triggerMessageVoiceEditFlash = useCallback(() => {
    setMessageFlashEpoch(epoch => epoch + 1);
    if (messageFlashTimerRef.current)
      clearTimeout(messageFlashTimerRef.current);
    messageFlashTimerRef.current = setTimeout(() => {
      setMessageFlashEpoch(0);
      messageFlashTimerRef.current = null;
    }, VOICE_EDIT_FLASH_MS);
  }, []);
  // Guards the one-shot auto-generation so editing/clearing the message later
  // doesn't re-trigger it.
  const generatedForRef = useRef<string | null>(null);
  // True after the registrar TYPED in the Message textarea; the blur commit
  // only stamps edit attribution when a real change happened.
  const messageDirtyRef = useRef(false);

  // Open-TE check (create mode). Once an EHR patient is confirmed we look for an
  // already-open telephone encounter on that patient before revealing the rest
  // of the form, so the registrar can choose to add to it instead of creating a
  // duplicate.
  const [openCheckState, setOpenCheckState] = useState<
    'idle' | 'checking' | 'done'
  >('idle');
  const [openMatches, setOpenMatches] = useState<
    OpenTelephoneEncounterSummary[]
  >([]);
  const [createNewConfirmed, setCreateNewConfirmed] = useState(false);
  const openCheckedForRef = useRef<string | null>(null);
  // Guards the one-shot "seed provider/assignee from the patient's prior open
  // TE" so we only default the empty fields once per patient.
  const seededFromPriorForRef = useRef<string | null>(null);

  useEffect(() => {
    setTe(telephoneEncounter);
  }, [telephoneEncounter?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    setReferenceLoading(true);
    Promise.all([
      getReasonPresets(),
      getFacilities(),
      getProviders(companyId),
      getCareTeam(companyId),
    ])
      .then(([presetList, facilityList, providerList, careTeamList]) => {
        if (cancelled) return;
        setPresets(presetList);
        setFacilities(facilityList);
        setProviders(providerList);
        setCareTeam(careTeamList);
      })
      .catch(err => {
        console.error(
          '[TelephoneEncounterDetailView] reference load failed',
          err,
        );
      })
      .finally(() => {
        if (!cancelled) setReferenceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const isDraft = te.status === 'draft';
  const isLocked = te.status === 'closed';
  // CREATE while drafting (Reason + Message); EDIT once open/closed (Action
  // Taken thread, no reason/message composer).
  const mode: 'create' | 'edit' = isDraft ? 'create' : 'edit';

  // Create-mode gating: pick the patient -> check for an open TE -> reveal the
  // rest of the form (or offer to edit the open one).
  const hasEhrPatient = Boolean(te.ehrPatientId);
  const needsPatient = mode === 'create' && !hasEhrPatient;
  const isCheckingOpen =
    mode === 'create' && hasEhrPatient && openCheckState === 'checking';
  const hasOpenMatch = openMatches.length > 0 && !createNewConfirmed;
  const detailsUnlocked =
    mode === 'edit' ||
    (mode === 'create' &&
      hasEhrPatient &&
      openCheckState === 'done' &&
      !hasOpenMatch);

  // ECW requires a reason, a provider, and an assignee to create a telephone
  // encounter (the backend `createTelephoneEncounter` op rejects a missing
  // one). Gate Push on them so the registrar gets inline guidance up front
  // instead of a backend error after clicking Push.
  const missingForPush = [
    !te.reasonText.trim() ? 'a reason' : null,
    !te.providerId ? 'a provider' : null,
    !te.assignedToId ? 'an assignee' : null,
  ].filter(Boolean) as string[];
  const canPush = missingForPush.length === 0;

  // Run the open-TE check once per confirmed EHR patient.
  useEffect(() => {
    if (mode !== 'create') return undefined;
    const ehrPatientId = te.ehrPatientId;
    if (!ehrPatientId) return undefined;
    if (openCheckedForRef.current === ehrPatientId) return undefined;
    openCheckedForRef.current = ehrPatientId;
    setCreateNewConfirmed(false);
    setOpenMatches([]);
    setOpenCheckState('checking');
    let cancelled = false;
    findOpenTelephoneEncounters(ehrPatientId)
      .then(matches => {
        if (cancelled) return;
        setOpenMatches(
          (matches || []).filter(
            m => !te.ehrEncounterId || m.ehrEncounterId !== te.ehrEncounterId,
          ),
        );
      })
      .finally(() => {
        if (!cancelled) setOpenCheckState('done');
      });
    return () => {
      cancelled = true;
    };
  }, [mode, te.ehrPatientId, te.ehrEncounterId]);

  const commit = useCallback(
    async (next: TelephoneEncounter) => {
      setTe(next);
      onUpdated?.(next);
      try {
        const saved = await saveTelephoneEncounter(next);
        setTe(saved);
        onUpdated?.(saved);
      } catch (err) {
        console.error('[TelephoneEncounterDetailView] save failed', err);
      }
    },
    [onUpdated],
  );

  // Default the Provider + Assign to from the patient's most recent open
  // telephone encounter, so a new draft for a patient who already has one
  // carries the prior care team forward instead of starting empty. Only seeds
  // fields the registrar hasn't already set, and only once per patient.
  useEffect(() => {
    if (mode !== 'create') return;
    if (openCheckState !== 'done') return;
    const ehrPatientId = te.ehrPatientId;
    if (!ehrPatientId) return;
    if (seededFromPriorForRef.current === ehrPatientId) return;
    if (openMatches.length === 0) return;
    seededFromPriorForRef.current = ehrPatientId;
    // Don't overwrite anything the registrar already chose.
    if (te.providerId && te.assignedToId) return;
    // Most recent prior encounter wins.
    const prior = [...openMatches].sort((a, b) =>
      (b.createdAt || '').localeCompare(a.createdAt || ''),
    )[0];
    if (!prior) return;
    const nextProviderId = te.providerId ?? prior.providerId ?? null;
    const nextAssignedToId = te.assignedToId ?? prior.assignedToId ?? null;
    if (
      nextProviderId === te.providerId &&
      nextAssignedToId === te.assignedToId
    ) {
      return; // nothing new to carry forward
    }
    commit({
      ...te,
      providerId: nextProviderId,
      providerName: te.providerName ?? prior.providerName ?? null,
      assignedToId: nextAssignedToId,
      assignedToName: te.assignedToName ?? prior.assignedToName ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, openCheckState, openMatches, te.ehrPatientId]);

  // Auto-generate the message for a from-call draft that doesn't have one yet.
  // One attempt per TE; failures leave the field empty + editable.
  useEffect(() => {
    if (mode !== 'create') return;
    if (!detailsUnlocked) return; // wait until patient + open-TE check are done
    if (!te.sourceCallId) return;
    if (te.message.trim()) return;
    if (generatedForRef.current === te.id) return;
    generatedForRef.current = te.id;

    let cancelled = false;
    setIsGeneratingMessage(true);
    generateTelephoneEncounterText({
      callId: te.sourceCallId,
      patientName: te.patientName,
      reasonText: te.reasonText,
      language: te.translator?.language || null,
      // Only stamp the Opalite ID when the call was interpreted (translator
      // present) — un-interpreted calls don't carry the tag.
      opaliteId: te.translator ? te.id : undefined,
    })
      .then(message => {
        if (cancelled || !message) return;
        // Record which call this message summarizes so the edit view never
        // re-drafts the same call into an Action Taken note.
        commit({...te, message, messageSourceCallId: te.sourceCallId});
        setMessageJustGenerated(true);
      })
      .finally(() => {
        if (!cancelled) setIsGeneratingMessage(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [te.id, mode, detailsUnlocked]);

  const handleReasonChange = (
    reasonText: string,
    reasonCode: string | null,
  ) => {
    const next = {...te, reasonText, reasonCode};
    if (reasonCode) {
      commit(next);
    } else {
      setTe(next);
    }
  };

  const handleProviderChange = (member: CareTeamMember | null) => {
    commit({
      ...te,
      providerId: member?.id ?? null,
      providerName: member?.name ?? null,
    });
  };

  // Reuse the CareTeamSelect roster shape for the facility picker (id + name;
  // role blank so no secondary label renders).
  const facilityMembers: CareTeamMember[] = facilities.map(facility => ({
    id: facility.id,
    name: facility.name,
    role: '',
  }));

  const handleFacilityChange = async (member: CareTeamMember | null) => {
    const facility = member ? {id: member.id, name: member.name} : null;
    // Local-only draft (create): record the choice; it rides along on the
    // create payload at push time. No ECW round trip yet.
    if (!te.ehrEncounterId) {
      commit({
        ...te,
        facilityId: facility?.id ?? null,
        facilityName: facility?.name ?? null,
      });
      return;
    }
    // Already in ECW (edit): push the facility change, reverting the optimistic
    // selection if the update fails — same pattern as reassign.
    const previous = te;
    const optimistic = {
      ...te,
      facilityId: facility?.id ?? null,
      facilityName: facility?.name ?? null,
    };
    setTe(optimistic);
    onUpdated?.(optimistic);
    try {
      const saved = await setFacility(te.id, facility);
      setTe(saved);
      onUpdated?.(saved);
    } catch (err: any) {
      console.error('[TelephoneEncounterDetailView] set facility failed', err);
      setTe(previous);
      onUpdated?.(previous);
      toast({
        title: 'Facility change failed',
        description:
          err?.message ||
          'Could not change the facility on the telephone encounter in the EHR.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  // Shared voice-edit runner: send the current draft + the spoken instruction
  // to the backend LLM, which applies commands ("remove the telephone-encounter
  // line", "make it more verbose") or folds in new content and returns the full
  // revised text. On failure we toast and return null so the caller keeps the
  // existing draft instead of losing it. `mode: 'compose'` switches the backend
  // to the dictation formatter (the transcript IS the field's content).
  const runVoiceEdit = useCallback(
    async (
      currentText: string,
      instruction: string,
      kind: 'message' | 'action_taken',
      mode: 'edit' | 'compose' = 'edit',
    ): Promise<string | null> => {
      try {
        return await editTelephoneEncounterText({
          currentText,
          instruction,
          kind,
          mode,
          patientName: te.patientName,
          language: te.translator?.language || null,
        });
      } catch (err: any) {
        toast({
          title: mode === 'compose' ? 'Dictation failed' : 'Voice edit failed',
          description:
            err?.message ||
            (mode === 'compose'
              ? 'Could not format your dictation. Your draft was kept — try again.'
              : 'Could not apply your voice edit. Your draft was kept — try again.'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return null;
      }
    },
    [te.patientName, te.translator?.language, toast],
  );

  // Stamp who last changed the Message and when — rendered as the "Last
  // edited by …" caption and carried on the record for the planner event.
  const withMessageAttribution = useCallback(
    (next: TelephoneEncounter): TelephoneEncounter => ({
      ...next,
      messageEditedByName: currentUser?.name ?? null,
      messageEditedAt: new Date().toISOString(),
    }),
    [currentUser?.name],
  );

  // True while the Message is blank: the mic composes the field from scratch
  // (a provider dictating lab results) instead of editing existing text.
  const messageIsBlank = !te.message.trim();

  // Voice edit / dictate on the Message field. Blank field → compose mode
  // (the transcript is the content, formatted by the LLM); non-blank → the
  // usual voice-edit arbitration. Then persist with attribution.
  const handleDictatedMessage = async (instruction: string) => {
    const composeMode = messageIsBlank ? 'compose' : 'edit';
    const revised = await runVoiceEdit(
      te.message,
      instruction,
      'message',
      composeMode,
    );
    if (revised != null) {
      commit(withMessageAttribution({...te, message: revised}));
      triggerMessageVoiceEditFlash();
    }
  };

  // "Simplify for patient": rewrite the clinical Message at a 3rd-5th grade
  // reading level and append it as a labeled block the nurse reads to the
  // patient when calling with results. Re-running replaces the block.
  const [isSimplifying, setIsSimplifying] = useState(false);
  const handleSimplifyMessage = async () => {
    if (isSimplifying || !te.message.trim()) return;
    setIsSimplifying(true);
    try {
      const withBlock = await simplifyTelephoneEncounterMessage({
        message: te.message,
        patientName: te.patientName,
      });
      if (withBlock && withBlock !== te.message) {
        commit(withMessageAttribution({...te, message: withBlock}));
        triggerMessageVoiceEditFlash();
      }
    } catch (err: any) {
      toast({
        title: 'Simplify failed',
        description:
          err?.message ||
          'Could not create the patient-friendly version. Your message was kept — try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSimplifying(false);
    }
  };

  // Voice edit on the Action Taken composer draft. Passed down to the thread,
  // which supplies its own live draft text as `currentText`.
  const handleVoiceEditActionDraft = useCallback(
    (currentText: string, instruction: string) =>
      runVoiceEdit(currentText, instruction, 'action_taken'),
    [runVoiceEdit],
  );

  // Persist the patient link onto the originating company phone call's
  // conversation doc (Firestore), so the link is reflected on our side — not
  // just used transiently for generation/push. Mirrors SessionDetailPanel's
  // reassign. Best-effort; failures don't block the local selection.
  //
  // We also broadcast the link so the phone-call list and any open phone-call
  // detail update in real time (the list is a cached fetch with no Firestore
  // listener), keeping the original conversation in sync with the TE.
  const persistCallPatientLink = useCallback(
    (payload: {
      patientId: string | null;
      ehrPatientId: string | null;
      patientName: string;
    }) => {
      if (!te.sourceCallId) return;
      notifyPhoneCallPatientLink(te.sourceCallId, {
        patient_id: payload.patientId,
        ehr_patient_id: payload.ehrPatientId,
        patient_name: payload.patientName || null,
      });
      void patientService
        .reassignSessionPatient(te.sourceCallId, {
          ...payload,
          reason: 'telephone_encounter_patient_link',
        })
        .catch((err: any) => {
          console.warn(
            '[TelephoneEncounterDetailView] persisting call patient link failed',
            err,
          );
        });
    },
    [te.sourceCallId],
  );

  const handlePatientSelect = (item: any) => {
    if (!item) {
      commit({...te, patientId: null, ehrPatientId: null, patientName: null});
      persistCallPatientLink({
        patientId: null,
        ehrPatientId: null,
        patientName: '',
      });
      return;
    }
    const name = patientDisplayName(item) || null;
    const ehrPatientId = ehrService.resolveEhrPatientId(item);
    const localPatientId = item?.localPatient?.id || null;
    commit({
      ...te,
      patientId: localPatientId,
      ehrPatientId,
      patientName: name,
    });
    persistCallPatientLink({
      patientId: localPatientId,
      ehrPatientId,
      patientName: name || '',
    });
  };

  // Free-text rename of the already-linked patient (create mode). On blur we
  // persist the encounter, and — when the name actually changed — broadcast the
  // new name so the originating call's row in Recents (and any open phone-call
  // detail) updates immediately, the same way picking a patient does.
  const patientNameAtFocusRef = useRef<string | null>(null);
  const handlePatientNameFocus = () => {
    patientNameAtFocusRef.current = te.patientName || '';
  };
  const handlePatientNameBlur = () => {
    const before = patientNameAtFocusRef.current;
    patientNameAtFocusRef.current = null;
    commit(te);
    if (before != null && before !== (te.patientName || '')) {
      persistCallPatientLink({
        patientId: te.patientId,
        ehrPatientId: te.ehrPatientId,
        patientName: te.patientName || '',
      });
    }
  };

  // Debounced server-side staff search for the assign-to picker. `CareTeamSelect`
  // waits ~1s after the user stops typing before invoking this, so we never
  // fire one request per keystroke. Results are merged (deduped by id) into the
  // existing roster so the preloaded list and the currently-selected assignee
  // both stay intact. Stale responses are dropped via the sequence guard.
  const handleStaffSearch = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const seq = (staffSearchSeqRef.current += 1);
    setStaffSearching(true);
    try {
      const matches = await getAssignableStaff(trimmed);
      if (seq !== staffSearchSeqRef.current) return;
      setCareTeam(prev => {
        const byId = new Map(prev.map(member => [member.id, member]));
        for (const member of matches) {
          if (member.id) byId.set(member.id, member);
        }
        return Array.from(byId.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[TelephoneEncounterDetailView] staff search failed', err);
      }
    } finally {
      if (seq === staffSearchSeqRef.current) setStaffSearching(false);
    }
  }, []);

  const handleAssign = async (member: CareTeamMember | null) => {
    const previous = te;
    const optimistic = {
      ...te,
      assignedToId: member?.id ?? null,
      assignedToName: member?.name ?? null,
    };
    setTe(optimistic);
    onUpdated?.(optimistic);
    try {
      const saved = await assignTo(te.id, member);
      setTe(saved);
      onUpdated?.(saved);
    } catch (err: any) {
      // Revert the optimistic selection — the EHR reassign didn't land.
      console.error('[TelephoneEncounterDetailView] assign failed', err);
      setTe(previous);
      onUpdated?.(previous);
      toast({
        title: 'Reassign failed',
        description:
          err?.message ||
          'Could not reassign the telephone encounter in the EHR.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const handleAddAction = async (text: string) => {
    setAddingAction(true);
    try {
      const saved = await addAction(te.id, {
        text,
        authorId: currentUser?.id ?? null,
        authorName: currentUser?.name ?? null,
        authorRole: currentUser?.role ?? null,
      });
      setTe(saved);
      onUpdated?.(saved);
      return true;
    } catch (err: any) {
      console.error('[TelephoneEncounterDetailView] add action failed', err);
      toast({
        title: 'Action not saved',
        description:
          err?.message ||
          'Could not write the action taken to the EHR. Your text was kept — try again.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      return false;
    } finally {
      setAddingAction(false);
    }
  };

  const handlePush = async () => {
    setPushing(true);
    try {
      const saved = await pushToEhr(te.id, {
        author: {name: currentUser?.name, role: currentUser?.role},
      });
      setTe(saved);
      onUpdated?.(saved);
      setShowPushSuccess(true);
    } catch (err: any) {
      console.error('[TelephoneEncounterDetailView] push failed', err);
      toast({
        title: 'Push failed',
        description:
          err?.message || 'Could not push the telephone encounter to the EHR.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPushing(false);
    }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      const saved = await closeTelephoneEncounter(te.id);
      setTe(saved);
      onUpdated?.(saved);
    } catch (err: any) {
      console.error('[TelephoneEncounterDetailView] close failed', err);
      toast({
        title: 'Close failed',
        description:
          err?.message || 'Could not address & close the telephone encounter.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setClosing(false);
    }
  };

  const language = te.translator?.language || '';
  const dialect = te.translator?.dialect || '';
  const showLanguage = !!language && isNonEnglish(language);
  const languageLabel = showLanguage
    ? `${languageFlag(language)} ${dialect ? `${language} (${dialect})` : language}`
    : '';
  const actionCount = te.actions.length;
  // True when the current message already summarizes the call we have in hand
  // (our own from-call draft). In that case the message IS this call's record,
  // so the Action Taken note must not re-draft it (it would duplicate) nor
  // re-stamp the Opalite id (the message → action "OR" rule keeps one tag per
  // encounter). When false but a call is attached (an existing ECW encounter
  // adopted from the picker), the just-completed call drives the action note.
  const messageFromThisCall = Boolean(
    te.sourceCallId && te.messageSourceCallId === te.sourceCallId,
  );
  const canGenerateAction = Boolean(te.sourceCallId) && !messageFromThisCall;
  // Tag the action with the originating call id so the ECW record points back
  // to the Opalite session — only when that call was interpreted.
  const actionOpaliteId =
    te.translator && canGenerateAction ? te.sourceCallId : undefined;
  const summaryTitle =
    String(te.message || '').trim() ||
    String(te.reasonText || '').trim() ||
    'No telephone encounter summary recorded.';

  const detailMetaLineItems = [
    {key: 'patient', label: te.patientName || 'Unknown caller'},
    languageLabel ? {key: 'language', label: languageLabel} : null,
    // Attribution: who opened the record (the header already carries the
    // created date · time above the title).
    te.createdByName
      ? {key: 'createdBy', label: `Created by ${te.createdByName}`}
      : null,
    te.assignedToName
      ? {
          key: 'assignee',
          label: `Assigned to ${te.assignedToName}`,
          icon: TablerUsers,
        }
      : null,
  ].filter(Boolean) as {key: string; label: string; icon?: any}[];

  const statusBadge = (() => {
    if (te.status === 'open') {
      return (
        <Badge
          px={2.5}
          py={1}
          borderRadius="md"
          fontSize="sm"
          fontWeight="normal"
          textTransform="none"
          bg={draftBadgeBg}
          color={draftBadgeColor}
        >
          {STATUS_LABELS.open}
        </Badge>
      );
    }
    if (te.status === 'closed') {
      return (
        <Badge
          display="inline-flex"
          alignItems="center"
          gap={1.5}
          px={2.5}
          py={1}
          borderRadius="md"
          fontSize="sm"
          fontWeight="normal"
          textTransform="none"
          bg={draftBadgeBg}
          color={closedBadgeColor}
        >
          <Icon as={TablerLock} boxSize={3} />
          {STATUS_LABELS.closed}
        </Badge>
      );
    }
    return (
      <Badge
        px={2.5}
        py={1}
        borderRadius="md"
        fontSize="sm"
        fontWeight="normal"
        textTransform="none"
        bg={draftBadgeBg}
        color={draftBadgeColor}
      >
        {STATUS_LABELS.draft}
      </Badge>
    );
  })();

  const phonePill = (
    <MetaPill
      gap={1.5}
      px={2.5}
      py={1}
      fontSize="sm"
      borderRadius="md"
      bg={phonePillTone.bg}
      color={phonePillTone.color}
      borderColor={phonePillTone.borderColor}
    >
      <Icon as={TablerPhone} boxSize={3} />
      Phone
    </MetaPill>
  );

  const primaryAction =
    te.status === 'draft' && detailsUnlocked ? (
      <Tooltip
        label={
          canPush ? '' : `Add ${missingForPush.join(', ')} before pushing.`
        }
        isDisabled={canPush}
        placement="top"
        hasArrow
      >
        {/* span wrapper so the tooltip still shows while the button is disabled */}
        <Box as="span" display="inline-block" w={{base: '100%', md: 'auto'}}>
          <Button
            {...RECAP_PRIMARY_BUTTON_PROPS}
            size="md"
            fontSize="sm"
            w={{base: '100%', md: 'auto'}}
            leftIcon={<TablerSend size={16} aria-hidden="true" />}
            isLoading={pushing}
            loadingText="Pushing"
            isDisabled={!canPush}
            onClick={handlePush}
          >
            Push
          </Button>
        </Box>
      </Tooltip>
    ) : te.status === 'open' ? (
      <Button
        {...RECAP_GRAY_SECONDARY_BUTTON_PROPS}
        size="md"
        fontSize="sm"
        w={{base: '100%', md: 'auto'}}
        leftIcon={<TablerCircleCheck size={16} aria-hidden="true" />}
        isLoading={closing}
        loadingText="Closing"
        onClick={handleClose}
      >
        Address & close
      </Button>
    ) : null;

  // Patient row: a typeahead until an EHR patient is confirmed in create mode
  // (we need the EHR id to run the open-TE check); otherwise the linked patient
  // name read-only.
  const showPatientPicker = needsPatient;

  return (
    <Box minH="100%" bg={pageBg} pb={8}>
      <Box>
        {onBack ? (
          <DetailBackButton
            label="Back to encounters"
            onClick={onBack}
            mb={3}
          />
        ) : null}
        <VStack spacing={4} align="stretch">
          {/* Header card — mirrors the regular encounter detail header */}
          <TeSectionCard borderRadius="xl">
            <Flex
              justify="space-between"
              align={isNarrow ? 'stretch' : 'flex-start'}
              direction={isNarrow ? 'column' : 'row'}
              gap={isNarrow ? 3 : 4}
            >
              <VStack align="start" spacing={2} flex={1} minW={0} w="100%">
                {te.createdAt ? (
                  <Text
                    fontSize="md"
                    color={bodyText}
                    lineHeight="1.2"
                    fontWeight="normal"
                    sx={{fontVariantNumeric: 'tabular-nums'}}
                  >
                    {formatTimestamp(te.createdAt)}
                  </Text>
                ) : null}
                <Text
                  fontWeight="normal"
                  fontSize="2xl"
                  color={bodyText}
                  lineHeight="1.4"
                  overflowWrap="anywhere"
                >
                  {summaryTitle}
                </Text>
                {detailMetaLineItems.length > 0 ? (
                  <Flex
                    as="p"
                    fontSize="sm"
                    fontWeight="normal"
                    lineHeight="1.35"
                    color={subtleText}
                    align="center"
                    flexWrap="wrap"
                    columnGap={1.5}
                    rowGap={1}
                  >
                    {detailMetaLineItems.map((item, index) => (
                      <Box
                        as="span"
                        key={item.key}
                        display="inline-flex"
                        alignItems="center"
                        gap={1.5}
                        minW={0}
                      >
                        {index > 0 ? (
                          <Box as="span" aria-hidden="true">
                            •
                          </Box>
                        ) : null}
                        <Box
                          as="span"
                          display="inline-flex"
                          alignItems="center"
                          gap={1}
                          minW={0}
                        >
                          {item.icon ? (
                            <Box
                              as={item.icon}
                              boxSize="13px"
                              aria-hidden="true"
                              flexShrink={0}
                            />
                          ) : null}
                          <Box as="span" minW={0}>
                            {item.label}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Flex>
                ) : null}
              </VStack>

              <HStack
                spacing={2}
                align="center"
                alignSelf="flex-start"
                flexShrink={0}
                flexWrap="wrap"
                w={isNarrow ? '100%' : 'auto'}
                mt={isNarrow ? 1 : 0}
              >
                {phonePill}
                {statusBadge}
              </HStack>
            </Flex>
          </TeSectionCard>

          {referenceLoading ? (
            <TelephoneEncounterDetailSkeleton
              showInterpreter={Boolean(te.translator)}
              showTranscript={Boolean(te.sourceCallId)}
            />
          ) : (
            <VStack
              spacing={4}
              align="stretch"
              sx={TE_DETAIL_FADE_IN_SX}
              animation={TE_DETAIL_FADE_IN_ANIMATION}
            >
              {/* Details form — mirrors the New Encounter composer's row layout */}
              <Box as="section">
                <RecapSectionHeading label="Details" />
                <TeSectionCard>
                  <VStack spacing={4} align="stretch">
                    <DetailFormRow
                      label="Patient"
                      htmlFor={showPatientPicker ? undefined : 'te-patient'}
                      isFirst
                      alignTop={showPatientPicker}
                    >
                      {showPatientPicker ? (
                        <VStack align="stretch" spacing={2}>
                          <TelephoneEncounterPatientPicker
                            onSelect={handlePatientSelect}
                          />
                          {te.patientName ? (
                            <Text fontSize="sm" color={subtleText}>
                              Caller: {te.patientName}. Select the matching
                              patient to continue.
                            </Text>
                          ) : (
                            <Text fontSize="sm" color={subtleText}>
                              Select the patient to continue.
                            </Text>
                          )}
                        </VStack>
                      ) : (
                        <Input
                          id="te-patient"
                          value={te.patientName || ''}
                          onChange={e =>
                            setTe(prev => ({
                              ...prev,
                              patientName: e.target.value,
                            }))
                          }
                          onFocus={handlePatientNameFocus}
                          onBlur={handlePatientNameBlur}
                          isDisabled={isLocked || mode === 'edit'}
                          placeholder="Caller / patient name"
                          h="44px"
                          borderRadius="lg"
                          borderColor={borderColor}
                          color={textColor}
                          _hover={{borderColor}}
                          _focusVisible={styles.inputFocusStyles}
                        />
                      )}
                    </DetailFormRow>

                    {isCheckingOpen ? (
                      <HStack
                        spacing={2}
                        color={subtleText}
                        fontSize="sm"
                        pt={1}
                      >
                        <Spinner size="xs" />
                        <Text>Checking for an open telephone encounter…</Text>
                      </HStack>
                    ) : null}

                    {hasOpenMatch ? (
                      <Box
                        borderTopWidth="1px"
                        borderColor={borderColor}
                        pt={4}
                      >
                        <OpenTelephoneEncounterPicker
                          patientName={te.patientName}
                          matches={openMatches}
                          onEdit={summary =>
                            onEditExisting?.(summary, {
                              sourceCallId: te.sourceCallId,
                              translator: te.translator,
                            })
                          }
                          onCreateNew={() => setCreateNewConfirmed(true)}
                        />
                      </Box>
                    ) : null}

                    {detailsUnlocked ? (
                      <>
                        {facilityMembers.length > 1 ? (
                          <DetailFormRow label="Facility">
                            <CareTeamSelect
                              value={te.facilityId}
                              members={facilityMembers}
                              onChange={handleFacilityChange}
                              placeholder="Select a facility"
                              ariaLabel="Select a facility"
                              isDisabled={isLocked}
                            />
                          </DetailFormRow>
                        ) : null}

                        {mode === 'create' ? (
                          <DetailFormRow label="Reason" alignTop>
                            <ReasonField
                              value={te.reasonText}
                              reasonCode={te.reasonCode}
                              presets={presets}
                              isDisabled={isLocked}
                              onChange={handleReasonChange}
                              onBlur={() => commit(te)}
                            />
                          </DetailFormRow>
                        ) : null}

                        <DetailFormRow label="Provider" isRequired>
                          <CareTeamSelect
                            value={te.providerId}
                            members={providers}
                            onChange={handleProviderChange}
                            placeholder="Select a provider"
                            ariaLabel="Select a provider"
                            isDisabled={isLocked}
                            allowClear
                            clearLabel="No provider"
                          />
                        </DetailFormRow>

                        {mode === 'create' ? (
                          <DetailFormRow
                            label="Message"
                            htmlFor="te-message"
                            alignTop
                          >
                            <Box position="relative">
                              <Box position="relative">
                                <Textarea
                                  id="te-message"
                                  value={te.message}
                                  onChange={e => {
                                    setMessageJustGenerated(false);
                                    messageDirtyRef.current = true;
                                    setTe(prev => ({
                                      ...prev,
                                      message: e.target.value,
                                    }));
                                  }}
                                  onBlur={() => {
                                    // Only a typed change stamps attribution;
                                    // a focus-then-blur must not claim an edit.
                                    if (messageDirtyRef.current) {
                                      messageDirtyRef.current = false;
                                      commit(withMessageAttribution(te));
                                      return;
                                    }
                                    commit(te);
                                  }}
                                  onAnimationEnd={() =>
                                    setMessageJustGenerated(false)
                                  }
                                  sx={
                                    messageJustGenerated
                                      ? TE_GENERATED_REVEAL_SX
                                      : undefined
                                  }
                                  isDisabled={isLocked || isGeneratingMessage}
                                  placeholder="Message — e.g. Pt saw MD today, concerned about direction for treatment."
                                  rows={4}
                                  borderRadius="lg"
                                  borderColor={borderColor}
                                  color={textColor}
                                  fontSize="md"
                                  lineHeight="1.5"
                                  resize="vertical"
                                  _hover={{borderColor}}
                                  _focusVisible={styles.inputFocusStyles}
                                />
                                {messageFlashEpoch > 0 ? (
                                  <DictationEditFlash
                                    key={`te-message-flash-${messageFlashEpoch}`}
                                    borderRadius="lg"
                                  />
                                ) : null}
                              </Box>
                              {isGeneratingMessage ? (
                                <HStack
                                  spacing={2}
                                  mt={2}
                                  color={subtleText}
                                  fontSize="sm"
                                >
                                  <Spinner size="xs" />
                                  <Text>Generating message from the call…</Text>
                                </HStack>
                              ) : (
                                <Flex
                                  justify="space-between"
                                  align="center"
                                  gap={2}
                                  flexWrap="wrap"
                                  mt={2}
                                >
                                  <Box minW={0}>
                                    {te.messageEditedByName &&
                                    te.messageEditedAt ? (
                                      <Text
                                        fontSize="xs"
                                        color={subtleText}
                                        sx={{
                                          fontVariantNumeric: 'tabular-nums',
                                        }}
                                      >
                                        Last edited by {te.messageEditedByName}{' '}
                                        · {formatTimestamp(te.messageEditedAt)}
                                      </Text>
                                    ) : null}
                                  </Box>
                                  <HStack spacing={2} flexShrink={0}>
                                    {!messageIsBlank ? (
                                      <Button
                                        size="md"
                                        fontSize="sm"
                                        fontWeight="normal"
                                        borderRadius="full"
                                        variant="outline"
                                        leftIcon={
                                          <TablerWand
                                            size={16}
                                            aria-hidden="true"
                                          />
                                        }
                                        onClick={handleSimplifyMessage}
                                        isLoading={isSimplifying}
                                        loadingText="Simplifying"
                                        isDisabled={isLocked}
                                        aria-label="Add a patient-friendly explanation below the message"
                                      >
                                        Simplify for patient
                                      </Button>
                                    ) : null}
                                    <TeDictationButton
                                      onInsert={handleDictatedMessage}
                                      isDisabled={isLocked || isSimplifying}
                                      label={
                                        messageIsBlank
                                          ? 'Dictate'
                                          : 'Voice edit'
                                      }
                                      applyingLabel={
                                        messageIsBlank
                                          ? 'Formatting dictation…'
                                          : 'Applying voice edit…'
                                      }
                                      maxDurationMs={
                                        messageIsBlank
                                          ? MESSAGE_DICTATION_MAX_MS
                                          : undefined
                                      }
                                    />
                                  </HStack>
                                </Flex>
                              )}
                            </Box>
                          </DetailFormRow>
                        ) : null}

                        <DetailFormRow label="Assign to" isRequired>
                          <CareTeamSelect
                            value={te.assignedToId}
                            members={careTeam}
                            onChange={handleAssign}
                            onSearch={handleStaffSearch}
                            isSearching={staffSearching}
                            placeholder="Assign to a team member"
                            ariaLabel="Assign to a team member"
                            isDisabled={isLocked}
                            allowClear
                            clearLabel="Unassigned"
                          />
                        </DetailFormRow>
                      </>
                    ) : null}
                  </VStack>
                </TeSectionCard>
                {te.status === 'draft' && detailsUnlocked ? (
                  <Flex justify="flex-end" mt={3}>
                    {primaryAction}
                  </Flex>
                ) : null}
              </Box>

              {/* Action taken thread — only once the encounter is open (EDIT). */}
              {mode === 'edit' ? (
                <Box as="section">
                  <RecapSectionHeading
                    label="Action Taken"
                    badgeLabel={actionCount > 0 ? String(actionCount) : null}
                  />
                  <TeSectionCard>
                    <ActionTakenThread
                      actions={te.actions}
                      onAdd={handleAddAction}
                      onVoiceEdit={handleVoiceEditActionDraft}
                      onGenerate={
                        canGenerateAction
                          ? () =>
                              generateTelephoneEncounterText({
                                callId: te.sourceCallId,
                                kind: 'action_taken',
                                patientName: te.patientName,
                                reasonText: te.reasonText,
                                language: te.translator?.language || null,
                                opaliteId: actionOpaliteId,
                              })
                          : undefined
                      }
                      // Auto-draft the action from the just-completed call so
                      // the registrar lands on a ready-to-review note instead
                      // of a blank composer (suppressed when the message is
                      // already this call's summary — see canGenerateAction).
                      autoGenerate={canGenerateAction}
                      isLocked={isLocked}
                      isAdding={addingAction}
                    />
                  </TeSectionCard>
                  {te.status === 'open' ? (
                    <Flex justify="flex-end" mt={3}>
                      {primaryAction}
                    </Flex>
                  ) : null}
                </Box>
              ) : null}

              {/* Interpreter (read-only, auto-captured) */}
              {te.translator ? (
                <Box as="section">
                  <RecapSectionHeading label="Interpreter" />
                  <TeSectionCard>
                    <VStack align="stretch" spacing={2}>
                      <Flex justify="space-between" gap={3}>
                        <Text fontSize="md" color={subtleText}>
                          Service
                        </Text>
                        <Text fontSize="md" color={textColor}>
                          {te.translator.vendor || '—'}
                        </Text>
                      </Flex>
                      {te.translator.language ? (
                        <Flex justify="space-between" gap={3}>
                          <Text fontSize="md" color={subtleText}>
                            Language
                          </Text>
                          <Text fontSize="md" color={textColor} translate="no">
                            {te.translator.dialect
                              ? `${te.translator.language} (${te.translator.dialect})`
                              : te.translator.language}
                          </Text>
                        </Flex>
                      ) : null}
                      {te.translator.translatorId ? (
                        <Flex justify="space-between" gap={3}>
                          <Text fontSize="md" color={subtleText}>
                            Interpreter ID
                          </Text>
                          <Text
                            fontSize="md"
                            color={textColor}
                            sx={{fontVariantNumeric: 'tabular-nums'}}
                          >
                            {te.translator.translatorId}
                          </Text>
                        </Flex>
                      ) : null}
                    </VStack>
                  </TeSectionCard>
                </Box>
              ) : null}

              {/* Originating call transcript */}
              {te.sourceCallId ? (
                <Box as="section">
                  <RecapSectionHeading label="Transcript" />
                  <PhoneCallTranscript callId={te.sourceCallId} />
                </Box>
              ) : null}
            </VStack>
          )}
        </VStack>
      </Box>

      <TelephoneEncounterPushSuccessModal
        isOpen={showPushSuccess}
        ehrBrandLabel={ehrBrandLabel}
        patientName={te.patientName || ''}
        onClose={() => setShowPushSuccess(false)}
        onViewHistory={() => {
          setShowPushSuccess(false);
          onPushed?.(te);
        }}
      />
    </Box>
  );
};

export default TelephoneEncounterDetailView;
